import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";
import type {
  Project,
  GanttTask,
  ProjectStatus,
  TaskStatus,
  TaskPriority,
} from "./projects";

// ── Filter Types ──────────────────────────────────────────────────────────────

export interface CrossAccountProjectsFilters {
  search?: string;
  status?: ProjectStatus | "";
  ordering?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

export interface CrossAccountTasksFilters {
  project_id?: number;
  search?: string;
  status?: TaskStatus | "";
  priority?: TaskPriority | "";
  ordering?: string;
  page?: number;
  page_size?: number;
}

// ── Paginated Response Types ──────────────────────────────────────────────────

export interface PaginatedProjects {
  items: Project[];
  count: number;
  total_pages: number;
}

export interface PaginatedTasks {
  items: GanttTask[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Cross-Account Projects ────────────────────────────────────────────────────

const fetchCrossAccountProjects = async ({
  queryKey,
}: {
  queryKey: readonly [string, string, CrossAccountProjectsFilters];
}): Promise<PaginatedProjects> => {
  const [, , filters] = queryKey;

  const {
    search,
    status,
    ordering = "-created_at",
    start_date,
    end_date,
    page = 1,
    page_size = 10,
  } = filters || {};

  const params = new URLSearchParams();
  if (search && typeof search === "string" && search.trim().length > 0) {
    params.append("search", search.trim());
  }
  if (status) params.append("status", status);
  if (ordering) params.append("ordering", ordering);
  if (start_date && end_date) {
    params.append("start_date", start_date);
    params.append("end_date", end_date);
  }
  if (page) params.append("page", String(page));
  if (page_size) params.append("page_size", String(page_size));

  const { data } = await axiosInstance.get(endPoints.ProjectsList, { params });
  return data?.data;
};

export const useCrossAccountProjects = (
  filters: CrossAccountProjectsFilters = {},
  enabled = true,
) => {
  const filtersKey: CrossAccountProjectsFilters = {
    search: filters?.search || "",
    status: filters?.status || "",
    ordering: filters?.ordering || "-created_at",
    start_date: filters?.start_date || "",
    end_date: filters?.end_date || "",
    page: filters?.page || 1,
    page_size: filters?.page_size || 10,
  };

  return useQuery<PaginatedProjects>({
    queryKey: queryKeys.CrossAccountProjects(filtersKey),
    queryFn: fetchCrossAccountProjects,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Cross-Account Tasks ───────────────────────────────────────────────────────

const fetchCrossAccountTasks = async ({
  queryKey,
}: {
  queryKey: readonly [string, string, CrossAccountTasksFilters];
}): Promise<PaginatedTasks> => {
  const [, , filters] = queryKey;

  const {
    project_id,
    search,
    status,
    priority,
    ordering = "-updated_at",
    page = 1,
    page_size = 10,
  } = filters || {};

  const params = new URLSearchParams();
  if (project_id) params.append("project_id", String(project_id));
  if (search && typeof search === "string" && search.trim().length > 0) {
    params.append("search", search.trim());
  }
  if (status) params.append("status", status);
  if (priority) params.append("priority", priority);
  if (ordering) params.append("ordering", ordering);
  if (page) params.append("page", String(page));
  if (page_size) params.append("page_size", String(page_size));

  const { data } = await axiosInstance.get(endPoints.TasksList, { params });
  return data?.data;
};

export const useCrossAccountTasks = (
  filters: CrossAccountTasksFilters = {},
  enabled = true,
) => {
  const filtersKey: CrossAccountTasksFilters = {
    project_id: filters?.project_id,
    search: filters?.search || "",
    status: filters?.status || "",
    priority: filters?.priority || "",
    ordering: filters?.ordering || "-updated_at",
    page: filters?.page || 1,
    page_size: filters?.page_size || 10,
  };

  return useQuery<PaginatedTasks>({
    queryKey: queryKeys.CrossAccountTasks(filtersKey),
    queryFn: fetchCrossAccountTasks,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};
